import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plane, Calendar, CreditCard, MapPin, Info } from 'lucide-react';
import { Footer } from '../components/Footer';
import { ScrollToTop } from '../components/ScrollToTop';
export function PlanVisitPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const sections = [{
    icon: Plane,
    title: 'Getting There',
    color: 'bg-blue-500',
    content: [{
      subtitle: 'By Air',
      text: "Jomo Kenyatta International Airport (NBO) is East Africa's busiest airport, located 15km from the city center. Direct flights available from major cities worldwide including London, Dubai, Amsterdam, and Johannesburg."
    }, {
      subtitle: 'Airport Transfer',
      text: 'Uber, Bolt, and Little Cab operate from the airport (KES 1,500-2,500 to city center). Official airport taxis available at fixed rates. Many hotels offer complimentary airport pickup.'
    }]
  }, {
    icon: Calendar,
    title: 'Best Time to Visit',
    color: 'bg-green-500',
    content: [{
      subtitle: 'Dry Seasons (Best)',
      text: 'January-February and July-October offer the best weather for wildlife viewing and outdoor activities. Clear skies and minimal rainfall.'
    }, {
      subtitle: 'Rainy Seasons',
      text: 'March-May (long rains) and November-December (short rains). Fewer tourists, lower prices, and lush green landscapes. Wildlife still visible but roads can be muddy.'
    }]
  }, {
    icon: CreditCard,
    title: 'Money & Costs',
    color: 'bg-purple-500',
    content: [{
      subtitle: 'Currency',
      text: 'Kenyan Shilling (KES). US Dollars widely accepted at hotels and tourist sites. ATMs abundant in the city. Credit cards accepted at major establishments.'
    }, {
      subtitle: 'Daily Budget',
      text: 'Budget: KES 5,000-8,000 | Mid-range: KES 10,000-20,000 | Luxury: KES 30,000+. Includes accommodation, meals, transport, and activities.'
    }]
  }, {
    icon: MapPin,
    title: 'Getting Around',
    color: 'bg-orange-500',
    content: [{
      subtitle: 'Ride-Hailing',
      text: 'Uber, Bolt, and Little Cab are safe, affordable, and widely available. Download apps before arrival. Typical city ride: KES 300-800.'
    }, {
      subtitle: 'Matatus',
      text: 'Local minibus transport. Cheap (KES 50-100) but can be crowded. An authentic Nairobi experience for adventurous travelers.'
    }, {
      subtitle: 'Car Rental',
      text: 'Available from KES 5,000/day. International license required. Traffic can be heavy during rush hours (7-9 AM, 5-7 PM).'
    }]
  }];
  const essentials = ['Valid passport (6 months validity)', 'Visa (available online or on arrival for most nationalities)', 'Yellow fever certificate (if arriving from endemic countries)', 'Travel insurance recommended', 'Light layers for cool mornings/evenings', 'Sun protection (hat, sunscreen, sunglasses)', 'Comfortable walking shoes', 'Power adapter (UK-style 3-pin plugs, 240V)'];
  const safetyTips = ['Use registered taxis or ride-hailing apps', 'Avoid displaying expensive jewelry or electronics', 'Keep copies of important documents', 'Stay in well-lit, populated areas at night', 'Use hotel safes for valuables', 'Tap water is safe to drink', 'Be aware of surroundings in crowded areas', 'Have emergency contacts saved'];
  return <div className="min-h-screen bg-white" data-id="element-1352">
      {/* Hero */}
      <div className="border-b border-gray-200" data-id="element-1353">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-20 md:py-32" data-id="element-1354">
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6
        }} className="max-w-4xl" data-id="element-1355">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1356"></div>
            <h1 className="text-7xl md:text-9xl font-light text-gray-900 mb-6 leading-[0.9] tracking-tight" data-id="element-1357">
              Plan Your
              <br data-id="element-1358" />
              <span className="font-serif italic" data-id="element-1359">Visit</span>
            </h1>
            <p className="text-2xl md:text-3xl text-gray-600 leading-relaxed font-light max-w-2xl" data-id="element-1360">
              Everything you need to know for an unforgettable trip to Nairobi.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 lg:px-12 py-16" data-id="element-1361">
        {/* Main Sections */}
        <div className="space-y-16 mb-20" data-id="element-1362">
          {sections.map((section, index) => {
          const Icon = section.icon;
          return <motion.section key={section.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-1363">
                <div className="flex items-center gap-4 mb-8" data-id="element-1364">
                  <div className={`w-12 h-12 ${section.color} flex items-center justify-center`} data-id="element-1365">
                    <Icon className="w-6 h-6 text-white" strokeWidth={1.5} data-id="element-1366" />
                  </div>
                  <h2 className="text-4xl font-light text-gray-900 tracking-tight" data-id="element-1367">
                    {section.title}
                  </h2>
                </div>

                <div className="space-y-6" data-id="element-1368">
                  {section.content.map((item, i) => <div key={i} className="border-l-4 border-amber-500 pl-6 py-2" data-id="element-1369">
                      <h3 className="text-xl font-light text-gray-900 mb-2" data-id="element-1370">
                        {item.subtitle}
                      </h3>
                      <p className="text-gray-700 leading-relaxed font-light" data-id="element-1371">
                        {item.text}
                      </p>
                    </div>)}
                </div>
              </motion.section>;
        })}
        </div>

        {/* Essentials Checklist - NO CHECKBOXES */}
        <motion.section className="mb-16 py-12 bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-1372">
          <div className="px-8" data-id="element-1373">
            <div className="flex items-center gap-4 mb-8" data-id="element-1374">
              <div className="w-12 h-12 bg-blue-500 flex items-center justify-center" data-id="element-1375">
                <Info className="w-6 h-6 text-white" strokeWidth={1.5} data-id="element-1376" />
              </div>
              <h2 className="text-4xl font-light text-gray-900 tracking-tight" data-id="element-1377">
                Essential Checklist
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4" data-id="element-1378">
              {essentials.map((item, index) => <div key={index} className="flex items-start gap-3 p-4 bg-white border border-blue-200" data-id="element-1379">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0 mt-2" data-id="element-1380" />
                  <span className="text-gray-700 font-light" data-id="element-1381">{item}</span>
                </div>)}
            </div>
          </div>
        </motion.section>

        {/* Safety Tips */}
        <motion.section className="mb-16 py-12 bg-gradient-to-br from-green-50 to-teal-50 border border-green-200" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-1382">
          <div className="px-8" data-id="element-1383">
            <div className="flex items-center gap-4 mb-8" data-id="element-1384">
              <div className="w-12 h-12 bg-green-500 flex items-center justify-center" data-id="element-1385">
                <Info className="w-6 h-6 text-white" strokeWidth={1.5} data-id="element-1386" />
              </div>
              <h2 className="text-4xl font-light text-gray-900 tracking-tight" data-id="element-1387">
                Safety Tips
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4" data-id="element-1388">
              {safetyTips.map((tip, index) => <div key={index} className="flex items-start gap-3 p-4 bg-white border border-green-200" data-id="element-1389">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0 mt-2" data-id="element-1390" />
                  <span className="text-gray-700 font-light" data-id="element-1391">{tip}</span>
                </div>)}
            </div>
          </div>
        </motion.section>

        {/* Emergency Contacts - REMOVED TOURIST HELPLINE */}
        <motion.section className="p-8 bg-gradient-to-br from-red-500 to-orange-500 text-white" initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} data-id="element-1392">
          <h2 className="text-4xl font-light mb-8 tracking-tight" data-id="element-1393">
            Emergency Contacts
          </h2>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-1394">
            <div className="p-6 bg-white/10 backdrop-blur-sm border border-white/20" data-id="element-1395">
              <h3 className="font-light mb-2 text-lg" data-id="element-1396">Police</h3>
              <p className="text-3xl font-light" data-id="element-1397">999 / 112</p>
            </div>
            <div className="p-6 bg-white/10 backdrop-blur-sm border border-white/20" data-id="element-1398">
              <h3 className="font-light mb-2 text-lg" data-id="element-1399">Ambulance</h3>
              <p className="text-3xl font-light" data-id="element-1400">999 / 112</p>
            </div>
          </div>
        </motion.section>
      </div>

      <ScrollToTop data-id="element-1401" />
      <Footer data-id="element-1402" />
    </div>;
}